import { useState } from 'react';
import { mormSigner, AddressResult, ChainType, MultichainAddressResult } from '../sdk';
import { generateAddressFromMnemonic as generateAddressFromMnemonicUtil } from '../utils/addressGeneration';

/**
 * Custom hook for managing multichain address generation from mnemonic phrases
 * 
 * @param initialMnemonic - Initial mnemonic phrase value (default: standard test mnemonic)
 * @param initialIndex - Initial address index value (default: 0)
 * @param initialChainType - Initial chain type (default: 'ethereum')
 * @param initialIsMainnet - Initial network type (default: true)
 * @returns Object containing:
 *   - mnemonic: Current mnemonic phrase
 *   - setMnemonic: Function to update mnemonic phrase
 *   - addressIndex: Current address index
 *   - setAddressIndex: Function to update address index
 *   - chainType: Current chain type
 *   - setChainType: Function to update chain type
 *   - isMainnet: Current network type (mainnet/testnet)
 *   - setIsMainnet: Function to update network type
 *   - result: Generated address result or error (supports both AddressResult and MultichainAddressResult)
 *   - loading: Boolean indicating if address generation is in progress
 *   - generateAddress: Function to trigger address generation (uses multichain if chainType is not ethereum)
 *   - isReady: Boolean indicating if mormSigner is ready
 */
export function useAddressGenerator(
  initialMnemonic: string = 'abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon about',
  initialIndex: number = 0,
  initialChainType: ChainType = 'ethereum',
  initialIsMainnet: boolean = true
) {
  const [mnemonic, setMnemonic] = useState<string>(initialMnemonic);
  const [addressIndex, setAddressIndex] = useState<number>(initialIndex);
  const [chainType, setChainType] = useState<ChainType>(initialChainType);
  const [isMainnet, setIsMainnet] = useState<boolean>(initialIsMainnet);
  const [result, setResult] = useState<AddressResult | MultichainAddressResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Uses shared address generation utility to eliminate duplication
  const generateAddress = async (): Promise<void> => {
    setLoading(true);
    setResult(null);

    try {
      const result = await generateAddressFromMnemonicUtil(
        mnemonic,
        chainType,
        addressIndex,
        isMainnet
      );
      setResult(result);
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    mnemonic,
    setMnemonic,
    addressIndex,
    setAddressIndex,
    chainType,
    setChainType,
    isMainnet,
    setIsMainnet,
    result,
    loading,
    generateAddress,
    isReady: mormSigner.isReady()
  };
}
